import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core'
import { FlexModule } from '@angular/flex-layout'
import { ControlValueAccessor, FormBuilder, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatRadioModule } from '@angular/material/radio'
import { MatSnackBar } from '@angular/material/snack-bar'
import { AppointmentAuthorizationService, ClinicModulesKeys, ClinicService, PORTAL_QUESTION_TYPE, PortalModel, PortalQuestion } from '@mmx/shared'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { BehaviorSubject, map, Subject, takeUntil } from 'rxjs'

@Component({
  selector: 'mmx-auth-portal-questions-form',
  templateUrl: './auth-portal-questions-form.component.html',
  styleUrls: ['./auth-portal-questions-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatCheckboxModule,
    MatRadioModule,
    MatInputModule,
    FlexModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: AuthPortalQuestionsFormComponent,
      multi: true,
    },
  ],
})
export class AuthPortalQuestionsFormComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @Input()
  public authorizationId: string

  @Input()
  public questions: PortalQuestion[] = []

  @Input()
  public portal: PortalModel

  public error$ = new BehaviorSubject<string>(null)
  public currentAnswerStep$ = new BehaviorSubject<string>(null)
  public formGroup = this.fb.group({})
  public questionsWithNoAnswers = []

  public get canUseAi() {
    return this.clinicService.clinic.modules[ClinicModulesKeys.AUTHORIZATION_AI]
  }

  private onChange: Function
  private onTouched: Function
  private onDestroy$ = new Subject<void>()

  constructor(
    private fb: FormBuilder,
    private authorizationSvc: AppointmentAuthorizationService,
    private snackbar: MatSnackBar,
    private t: TranslateService,
    private clinicService: ClinicService,
  ) { }

  public ngOnInit() {
    this.formGroup.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
      )
      .subscribe((value) => {
        this.onChange?.({
          portalId: this.portal.id,
          questionAnswers: value,
        })
      })
  }

  public ngOnChanges() {
    this.setQuestions()
  }

  public ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public writeValue(value: Record<string, any>) {
    if (!Object.keys(value || {}).length) {
      return
    }

    this.setQuestions(value)
  }

  public registerOnChange(fn: Function) {
    this.onChange = fn
  }

  public registerOnTouched(fn: Function) {
    this.onTouched = fn
  }

  public onAnswerQuestions(portal: PortalModel) {
    if (!this.canUseAi) {
      return
    }

    this.currentAnswerStep$.next('initial')
    this.error$.next(null)
    this.authorizationSvc.getAuthQuestionAnswers({
      authorizationId: this.authorizationId,
      portalId: portal.id,
    })
      .pipe(
        takeUntil(this.onDestroy$),
        map(({ data }) => data),
      )
      .subscribe(({ answers, step, complete }) => {
        const formattedAnswers = answers.reduce((acc: Record<string, any>, a) => ({
          ...acc,
          [a.questionId]: a.hasAnswer ? a.answer : 'no-answer',
        }), {})

        if (complete) {
          this.setQuestions(formattedAnswers)
        } else {
          this.fillAnswers(formattedAnswers)
        }

        if (step) {
          if (step === 'error') {
            this.error$.next('dialog.authorizations.portal-questions.error-answering-questions')
            this.currentAnswerStep$.next(null)
          } else {
            this.currentAnswerStep$.next(step)
            this.error$.next(null)
          }
        }

        if (complete) {
          this.currentAnswerStep$.next(null)
          this.snackbar.open(
            this.t.instant('dialog.authorizations.portal-questions.questions-answered'),
            null,
            { duration: 3000 },
          )
        }
      })
  }

  public getHint(question: PortalQuestion) {
    if (this.questionsWithNoAnswers.includes(question.id)) {
      return 'dialog.authorizations.portal-questions.no-answer-hint'
    }

    return ''
  }

  private setQuestions(answers: Record<string, any> = {}) {
    this.questionsWithNoAnswers = []
    if (!this.questions.length || !this.portal) {
      return
    }

    Object.keys(this.formGroup.controls).forEach((key) => this.formGroup.removeControl(key))

    for (const question of this.questions) {
      const answer = this.getQuestionAnswer(question.id, answers)

      if (question.type === PORTAL_QUESTION_TYPE.MULTISELECT) {
        question.answers?.forEach((a, i) => {
          const controlName = this.getRadioControlName(question.id, i)
          const optionAnswer = answer ? answer.includes(a) : this.getQuestionAnswer(controlName, answers)
          this.formGroup.addControl(controlName, this.fb.control(optionAnswer))
        })
      } else {
        this.formGroup.addControl(question.id, this.fb.control(answer))
      }
    }
  }

  private fillAnswers(answers: Record<string, any>) {
    for (const question of this.questions) {
      const answer = this.getQuestionAnswer(question.id, answers)
      if (!answer) {
        continue
      }

      if (question.type === PORTAL_QUESTION_TYPE.MULTISELECT) {
        question.answers?.forEach((a, i) => {
          const control = this.formGroup.get<string>(this.getRadioControlName(question.id, i))
          if (!control) {
            return
          }

          control.patchValue(answer?.includes(a))
        })
      } else {
        this.formGroup.get(question.id).patchValue(answer)
      }
    }
  }

  private getQuestionAnswer(questionId: string, answers: Record<string, any>) {
    let answer = answers[questionId]
    if (answer === 'no-answer') {
      this.questionsWithNoAnswers.push(questionId)
      answer = null
    }

    return answer
  }

  private getRadioControlName(questionId: string, i: number) {
    return `${questionId}--${i}`
  }
}
